import { Assets, AssetsManifest } from "pixi.js";
import { BundleResourcesPackage } from "./BundleResourcesPackage";

export class ResourceManager {
    private readonly packages: Map<String, BundleResourcesPackage>;
    private manifest: AssetsManifest;

    private constructor() {
        this.packages = new Map();
    }

    public registerResourcePackage<T extends BundleResourcesPackage>(resourcePackage: T): void {
        let key = resourcePackage.constructor.name;
        if (this.packages.has(key)) {
            throw new Error(`Can't register resource package ${key}. Already have one registered.`);
        }
        if (this.manifest.bundles.find((bundle) => bundle.name === key) == null) {
            throw new Error(`Bundle ${key} not exist in manifest.`);
        }
        this.packages.set(key, resourcePackage);
    }

    public async getResourcePackage<T extends BundleResourcesPackage>(clsT: new () => T): Promise<T> {
        const key = clsT.name;
        if (!this.packages.has(key)) {
            throw new Error(`Package ${key} not registered. Try register it before using.`);
        }
        let resourcePackage = this.packages.get(key);
        if (resourcePackage.loaded) {
            return resourcePackage as T;
        } else {
            const pkg = await resourcePackage.load();
            return pkg as T;
        }
    }

    private async initManifest(manifestPath: string): Promise<void> {
        try {
            this.manifest = await (await fetch(manifestPath)).json();
            await Assets.init({ manifest: this.manifest });
        }
        catch (e: unknown) {
            console.error("Manifest initialization failed", e);
        }
    }

    public static async initWithManifest(manifestPath: string): Promise<ResourceManager> {
        if (manifestPath == null || manifestPath.length === 0) {
            throw new Error("Manifest path cant be null or empty");
        }
        const resourceManager = new ResourceManager();
        await resourceManager.initManifest(manifestPath);
        return resourceManager;
    }
}
